/**
 * DataAnalyst.jsx
 * Unified AI data analyst for King's Academy.
 * Supports SurveyMonkey (from Firestore) + Canvas (live API).
 * All data passes through a server-side PII firewall before reaching the LLM.
 * PDF export: per-result and full session report.
 */

import { useState, useEffect, useCallback, useRef } from "react";
import { getFunctions, httpsCallable } from "firebase/functions";
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from "recharts";

import { getApp } from "firebase/app";
const functions = getFunctions(getApp(), "us-central1");
const fnAnalyze   = httpsCallable(functions, "dataAnalyst");
const fnSources   = httpsCallable(functions, "dataAnalystListSources");
const fnInvalidate = httpsCallable(functions, "dataAnalystInvalidateCache");

// ─── Design tokens ─────────────────────────────────────────────────────────
const FONT_DISPLAY = "'Playfair Display', Georgia, serif";
const FONT_DATA    = "'Rajdhani', 'Trebuchet MS', sans-serif";
const FONT_BODY    = "'EB Garamond', Georgia, serif";

const C = {
  ink:    "#1a1714",
  cream:  "#faf7f2",
  gold:   "#b8924a",
  navy:   "#1a3a5c",
  muted:  "#7a7068",
  border: "#e8e2d8",
  bg:     "#f4f0e8",
  green:  "#22863a",
  red:    "#8b2c2c",
};

const CHART_COLORS = ["#1a3a5c","#b8924a","#22863a","#8b2c2c","#6b5b95","#4a90a4","#d4a853","#2d6a4f"];

// PDF color arrays
const NAVY_P  = [26, 58, 92];
const GOLD_P  = [184, 146, 74];
const INK_P   = [26, 23, 20];
const MUTED_P = [122, 112, 104];
const GREEN_P = [34, 134, 58];

// ─── PDF helpers ───────────────────────────────────────────────────────────
const getJSPDF = async () => {
  if (window.jspdf?.jsPDF) return window.jspdf.jsPDF;
  await new Promise((res, rej) => {
    const s = document.createElement("script");
    s.src = "https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js";
    s.onload = res; s.onerror = rej;
    document.head.appendChild(s);
  });
  if (!window.jspdf?.jsPDF) throw new Error("jsPDF failed to load");
  return window.jspdf.jsPDF;
};

const getHtml2Canvas = async () => {
  if (window.html2canvas) return window.html2canvas;
  await new Promise((res, rej) => {
    const s = document.createElement("script");
    s.src = "https://cdnjs.cloudflare.com/ajax/libs/html2canvas/1.4.1/html2canvas.min.js";
    s.onload = res; s.onerror = rej;
    document.head.appendChild(s);
  });
  if (!window.html2canvas) throw new Error("html2canvas failed to load");
  return window.html2canvas;
};

// ─── PDF page builder for a single result entry ───────────────────────────
async function buildResultPage(doc, W, H, entry, idx, total, chartNode) {
  const now = new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });

  // ── Header band ──
  doc.setFillColor(...NAVY_P); doc.rect(0, 0, W, 36, "F");
  doc.setFillColor(...GOLD_P); doc.rect(0, 36, W, 0.8, "F");

  doc.setTextColor(212, 172, 106); doc.setFontSize(7); doc.setFont("helvetica", "bold");
  doc.text("KING'S ACADEMY  ·  DATA ANALYST  ·  INTERNAL USE ONLY", W / 2, 10, { align: "center" });
  if (total > 1) doc.text(`Result ${idx + 1} of ${total}`, W - 14, 10, { align: "right" });

  doc.setTextColor(250, 247, 242); doc.setFontSize(13); doc.setFont("helvetica", "bold");
  doc.text(entry.question, W / 2, 22, { align: "center", maxWidth: W - 24 });

  doc.setTextColor(200, 191, 176); doc.setFontSize(8); doc.setFont("helvetica", "italic");
  doc.text(`${entry.sourceSummary?.map(s => s.label).join(" + ")}  ·  ${now}`, W / 2, 31, { align: "center" });

  // ── PII badge ──
  doc.setFillColor(13, 45, 14);
  doc.roundedRect(12, 41, W - 24, 9, 1, 1, "F");
  doc.setDrawColor(...GREEN_P);
  doc.roundedRect(12, 41, W - 24, 9, 1, 1, "S");
  doc.setTextColor(63, 185, 80); doc.setFontSize(7.5); doc.setFont("helvetica", "bold");
  doc.setFontSize(7); doc.text("PRIVACY NOTICE: Names, emails and contact info stripped server-side before analysis. SIS IDs retained for joins only.", W / 2, 46.5, { align: "center", maxWidth: W - 28 });

  // ── Source summary boxes ──
  const sources = entry.sourceSummary || [];
  const boxCount = Math.min(sources.length, 4);
  if (boxCount > 0) {
    const boxW = (W - 24 - (boxCount - 1) * 4) / boxCount;
    let bx = 12;
    sources.slice(0, 4).forEach(s => {
      // Use splitTextToSize so long titles wrap rather than clip
      doc.setFontSize(6); doc.setFont("helvetica", "normal");
      const labelLines = doc.splitTextToSize(s.label, boxW - 4);
      const boxH = 14 + labelLines.length * 4;
      doc.setFillColor(244, 240, 232); doc.roundedRect(bx, 55, boxW, boxH, 1, 1, "F");
      doc.setDrawColor(...GOLD_P); doc.roundedRect(bx, 55, boxW, boxH, 1, 1, "S");
      // Count
      doc.setTextColor(...INK_P); doc.setFontSize(11); doc.setFont("helvetica", "bold");
      if (s.records != null) doc.text(s.records.toLocaleString(), bx + boxW / 2, 63, { align: "center" });
      // Label (wrapped)
      doc.setFontSize(6); doc.setFont("helvetica", "normal"); doc.setTextColor(...MUTED_P);
      doc.text(labelLines, bx + boxW / 2, 68, { align: "center" });
      // "responses" tag
      if (s.records != null) {
        const tagY = 68 + labelLines.length * 4;
        doc.setFontSize(5.5);
        doc.text("responses", bx + boxW / 2, tagY, { align: "center" });
      }
      bx += boxW + 4;
    });
  }

  let y = sources.length > 0 ? 84 : 56;

  // Strip markdown bold/italic markers and [chart] placeholders from narrative
  const cleanNarrative = (entry.narrative || "")
    .replace(/\[chart\]/g, "")
    .replace(/\*\*(.+?)\*\*/g, "$1")
    .replace(/\*(.+?)\*/g, "$1")
    .trim();

  // ── Charts (rendered from DOM) ──
  // Only capture the chart portion of the card (not the full card including narrative)
  // Capture each chart individually so they don't get clipped
const chartNodes = chartNode?.querySelectorAll?.(".chart-capture-zone > div") || [];
for (const chartEl of chartNodes) {
  try {
    const h2c = await getHtml2Canvas();
    const canvas = await h2c(chartEl, {
        scale: 3,
        backgroundColor: "#f4f0e8",
        useCORS: true,
        logging: false,
        windowWidth: chartEl.scrollWidth,  // ← was 1200
        width: chartEl.offsetWidth,         // ← add this
        height: chartEl.offsetHeight,       // ← add this
    });
    const imgW = W - 24;
    const imgH = (imgW / canvas.width) * canvas.height;

    // If chart doesn't fit on remaining page, start a new one
    if (y + imgH > H - 20) {
      doc.addPage();
      addContinuationHeader(doc, W, entry.question);
      y = 22;
    }

    doc.addImage(canvas.toDataURL("image/png"), "PNG", 12, y, imgW, imgH);
    y += imgH + 8;
  } catch (e) {
    console.warn("Chart capture failed:", e.message);
  }
}

  // ── Narrative analysis ──
  if (y > H - 50) { doc.addPage(); addContinuationHeader(doc, W, entry.question); y = 20; }

  // Section label
  doc.setFillColor(...GOLD_P); doc.rect(12, y, 2.5, 6, "F");
  doc.setFontSize(8); doc.setFont("helvetica", "bold"); doc.setTextColor(...NAVY_P);
  doc.text("AI ANALYSIS  —  ANONYMIZED DATA ONLY", 18, y + 4.5);
  y += 12;

  // Render narrative with basic paragraph awareness
  const paragraphs = cleanNarrative.split(/\n\n+/);
  doc.setFontSize(9); doc.setFont("helvetica", "normal"); doc.setTextColor(...INK_P);
  for (const para of paragraphs) {
    const lines = doc.splitTextToSize(para.trim(), W - 28);
    for (const line of lines) {
      if (y > H - 20) { doc.addPage(); addContinuationHeader(doc, W, entry.question); y = 20; }
      doc.text(line, 14, y);
      y += 5;
    }
    y += 3; // paragraph gap
  }
}

function addContinuationHeader(doc, W, title) {
  doc.setFillColor(...NAVY_P); doc.rect(0, 0, W, 12, "F");
  doc.setFillColor(...GOLD_P); doc.rect(0, 12, W, 0.5, "F");
  doc.setTextColor(212, 172, 106); doc.setFontSize(7); doc.setFont("helvetica", "bold");
  doc.text(`${title.slice(0, 80)}${title.length > 80 ? "…" : ""}  (continued)`, W / 2, 8, { align: "center" });
}

function addFooters(doc, W, H) {
  const pc = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pc; i++) {
    doc.setPage(i);
    doc.setFillColor(244, 240, 232); doc.rect(0, H - 10, W, 10, "F");
    doc.setFontSize(7); doc.setFont("helvetica", "italic"); doc.setTextColor(...MUTED_P);
    doc.text("Internal use only · King's Academy Data Analyst · PII-Free Report", W / 2, H - 4, { align: "center" });
    doc.text(`${i} / ${pc}`, W - 14, H - 4);
  }
}

// ─── Chart renderer (screen) ───────────────────────────────────────────────
function ChartBlock({ chart }) {
  if (!chart) return null;
  const { type, title, labels = [], datasets = [] } = chart;

  const chartData = labels.map((label, i) => {
    const point = { label };
    datasets.forEach(ds => { point[ds.label || "value"] = ds.data?.[i] ?? 0; });
    return point;
  });

  const pieData = labels.map((label, i) => ({
    name: label,
    value: datasets[0]?.data?.[i] ?? 0,
  }));

  return (
    <div style={{ background: C.bg, border: `1px solid ${C.border}`, borderRadius: 3, padding: "16px 20px", marginBottom: 16 }}>
      {title && (
        <div style={{ fontFamily: FONT_DATA, fontSize: 11, fontWeight: 600, letterSpacing: "0.12em", textTransform: "uppercase", color: C.navy, marginBottom: 12 }}>
          {title}
        </div>
      )}
      <ResponsiveContainer width="100%" height={type === "pie" ? 420 : labels.length > 5 ? 380 : 320}>
        {type === "pie" ? (() => {
          return (
            <PieChart margin={{ top: 10, right: 20, bottom: 10, left: 20 }}>
              <Pie
                data={pieData}
                dataKey="value"
                nameKey="name"
                cx="50%"
                cy="42%"
                outerRadius={110}
                label={false}
                labelLine={false}
              >
                {pieData.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
              </Pie>
              <Tooltip formatter={(value) => {
                const total = pieData.reduce((s, d) => s + d.value, 0);
                return [`${value} (${((value / total) * 100).toFixed(1)}%)`, ""];
              }} />
              <Legend
                verticalAlign="bottom"
                layout="vertical"
                align="center"
                formatter={(value) => {
                  const total = pieData.reduce((s, d) => s + d.value, 0);
                  const d = pieData.find(p => p.name === value);
                  return d ? `${value} (${((d.value / total) * 100).toFixed(1)}%)` : value;
                }}
                wrapperStyle={{ fontFamily: "'Rajdhani', sans-serif", fontSize: 12, paddingTop: 8 }}
              />
            </PieChart>
          );
        })() : type === "line" ? (
          <LineChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" stroke={C.border} />
            <XAxis dataKey="label" tick={{ fontFamily: FONT_DATA, fontSize: 10 }} />
            <YAxis tick={{ fontFamily: FONT_DATA, fontSize: 10 }} />
            <Tooltip /><Legend />
            {datasets.map((ds, i) => (
              <Line key={i} type="monotone" dataKey={ds.label || "value"}
                stroke={CHART_COLORS[i % CHART_COLORS.length]} strokeWidth={2} dot={false} />
            ))}
          </LineChart>
        ) : (() => {
          const isSingleDataset = datasets.length === 1;
          const rotateTicks = labels.length > 5;

          if (isSingleDataset) {
            const ds = datasets[0];
            const dsKey = ds.label || "value";
            const usePerBarColors = !ds.color && !ds.fill;

            const coloredData = usePerBarColors
              ? chartData.map((point, i) => ({ ...point, _color: CHART_COLORS[i % CHART_COLORS.length] }))
              : chartData;

            const singleColor = ds.color || ds.fill || CHART_COLORS[0];

            const ColoredBar = (props) => {
              const { x, y, width, height, _color } = props;
              return <rect x={x} y={y} width={width} height={height} fill={_color || singleColor} />;
            };

            const legendPayload = usePerBarColors
              ? labels.map((label, i) => ({ value: label, type: "square", color: CHART_COLORS[i % CHART_COLORS.length] }))
              : [{ value: dsKey, type: "square", color: singleColor }];

            return (
              <BarChart data={coloredData} margin={{ bottom: rotateTicks ? 40 : 8 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={C.border} />
                <XAxis
                  dataKey="label"
                  tick={{ fontFamily: FONT_DATA, fontSize: 10, fill: C.muted }}
                  angle={rotateTicks ? -35 : 0}
                  textAnchor={rotateTicks ? "end" : "middle"}
                  interval={0}
                  height={rotateTicks ? 60 : 30}
                />
                <YAxis tick={{ fontFamily: FONT_DATA, fontSize: 10 }} />
                <Tooltip />
                <Legend
                  verticalAlign="bottom"
                  wrapperStyle={{ fontFamily: FONT_DATA, fontSize: 11, paddingTop: 8 }}
                  payload={legendPayload}
                />
                <Bar dataKey={dsKey} shape={<ColoredBar />} />
              </BarChart>
            );
          }

          return (
            <BarChart data={chartData} margin={{ bottom: rotateTicks ? 40 : 8 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={C.border} />
              <XAxis
                dataKey="label"
                tick={{ fontFamily: FONT_DATA, fontSize: 10, fill: C.muted }}
                angle={rotateTicks ? -35 : 0}
                textAnchor={rotateTicks ? "end" : "middle"}
                interval={0}
                height={rotateTicks ? 60 : 30}
              />
              <YAxis tick={{ fontFamily: FONT_DATA, fontSize: 10 }} />
              <Tooltip />
              <Legend verticalAlign="bottom" wrapperStyle={{ fontFamily: FONT_DATA, fontSize: 11 }} />
              {datasets.map((ds, i) => (
                <Bar key={i} dataKey={ds.label || "value"} fill={CHART_COLORS[i % CHART_COLORS.length]} />
              ))}
            </BarChart>
          );
        })()}
      </ResponsiveContainer>
    </div>
  );
}

// ─── Result card (screen) ──────────────────────────────────────────────────
function ResultCard({ entry, idx, total, onDownload, pdfLoading, cardRef }) {
  return (
    <div style={{ background: "#fff", border: `1px solid ${C.border}`, borderRadius: 3, marginBottom: 20, overflow: "hidden", boxShadow: "0 1px 8px rgba(0,0,0,0.06)" }}>

      {/* Header */}
      <div style={{ background: C.bg, borderBottom: `1px solid ${C.border}`, padding: "12px 20px", display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, flexWrap: "wrap" }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontFamily: FONT_DATA, fontSize: 9, letterSpacing: "0.18em", textTransform: "uppercase", color: C.muted, marginBottom: 4, display: "flex", alignItems: "center", gap: 8 }}>
            <span>Result {idx + 1} of {total}  ·  {entry.sourceSummary?.map(s => s.label).join(" + ")}</span>
            {entry.fromCache && (
              <span title={`Cached answer · ${Math.round((entry.similarity || 0) * 100)}% match to: "${entry.cachedQuestion}"`}
                style={{ background: "#e8f4e8", color: C.green, border: `1px solid ${C.green}`, borderRadius: 10, padding: "1px 8px", fontSize: 9, fontWeight: 600, textTransform: "none", letterSpacing: 0 }}>
                ⚡ Cached
              </span>
            )}
          </div>
          <div style={{ fontFamily: FONT_DISPLAY, fontSize: "1rem", fontWeight: 600, color: C.ink }}>
            {entry.question}
          </div>
        </div>
        <button
          onClick={() => onDownload(idx)}
          disabled={pdfLoading}
          style={{ fontFamily: FONT_DATA, fontSize: 11, fontWeight: 600, padding: "6px 14px", border: `1px solid ${C.navy}`, borderRadius: 2, background: pdfLoading ? C.muted : C.navy, color: C.cream, flexShrink: 0, opacity: pdfLoading ? 0.7 : 1 }}
        >
          {pdfLoading ? "Building…" : "↓ Export PDF"}
        </button>
      </div>

      {/* Body — this div is captured for PDF */}
      <div ref={cardRef} style={{ padding: "16px 20px" }}>
        {/* PII notice */}
        <div style={{ background: "#0d2d0e", border: "1px solid #238636", borderRadius: 2, padding: "7px 12px", marginBottom: 14, fontFamily: FONT_DATA, fontSize: "0.72rem", color: "#3fb950", display: "flex", gap: 8 }}>
          <span>🔒</span>
          <span>All student names, emails and identifiers stripped before analysis. SIS IDs retained for joins only.</span>
        </div>

        {/* Charts — this zone is captured for PDF export */}
        {entry.charts?.length > 0 && (
          <div className="chart-capture-zone">
            {entry.charts.map((chart, i) => <ChartBlock key={i} chart={chart} />)}
          </div>
        )}

        {/* Narrative */}
        <div style={{ fontFamily: FONT_BODY, fontSize: 14, lineHeight: 1.85, color: C.ink, whiteSpace: "pre-wrap" }}>
          {entry.narrative?.replace(/\[chart\]/g, "").trim()}
        </div>
      </div>
    </div>
  );
}

// ─── Survey checkbox ───────────────────────────────────────────────────────
function SurveyCheckbox({ survey, checked, onChange }) {
  const disabled = !survey.available;
  return (
    <label style={{ display: "flex", alignItems: "flex-start", gap: 8, padding: "8px 0", borderBottom: `1px solid ${C.border}`, opacity: disabled ? 0.45 : 1, cursor: disabled ? "not-allowed" : "pointer" }}>
      <input type="checkbox" checked={checked}
        onChange={e => !disabled && onChange(survey.id, e.target.checked)}
        disabled={disabled} style={{ marginTop: 3, accentColor: C.navy }} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontFamily: FONT_DATA, fontSize: 12, fontWeight: 600, color: disabled ? C.muted : C.ink, lineHeight: 1.3 }}>
          {survey.title}
        </div>
        <div style={{ fontFamily: FONT_DATA, fontSize: 10, color: C.muted, marginTop: 2 }}>
          {disabled
            ? survey.responseCount === 0 ? "No responses pulled" : "Not yet pulled"
            : `${survey.responseCount?.toLocaleString()} responses · ${survey.questionCount} questions`
          }
        </div>
      </div>
    </label>
  );
}

// ─── Main component ────────────────────────────────────────────────────────
export default function DataAnalyst() {
  const [surveys,          setSurveys]          = useState([]);
  const [selectedSurveys,  setSelectedSurveys]  = useState(new Set());
  const [sourcesLoading,   setSourcesLoading]   = useState(true);
  const [question,         setQuestion]         = useState("");
  const [results,          setResults]          = useState([]);
  const [history,          setHistory]          = useState([]);
  const [analyzing,        setAnalyzing]        = useState(false);
  const [includeCharts,    setIncludeCharts]    = useState(false);
  const [forceRefresh,     setForceRefresh]     = useState(false);
  const [pdfLoading,       setPdfLoading]       = useState(false);
  const [error,            setError]            = useState("");
  const [showThread,       setShowThread]       = useState(false);
  const [log,              setLog]              = useState([]);

  const logRef    = useRef(null);
  const bottomRef = useRef(null);
  const cardRefs  = useRef([]);

  const addLog = (msg, type = "info") => {
    setLog(prev => [...prev, { msg, type, ts: new Date().toLocaleTimeString() }]);
    setTimeout(() => logRef.current?.scrollTo(0, 99999), 50);
  };

  useEffect(() => {
    (async () => {
        // ── TEMP: check custom claims ──
        const { getAuth } = await import("firebase/auth");
        const user = getAuth().currentUser;
        if (user) {
          const token = await user.getIdTokenResult(true);
          console.log("🔑 Custom claims:", JSON.stringify(token.claims));
        } else {
          console.log("🔑 No user logged in");
        }
        // ── end temp ──
      try {
        const { data } = await fnSources();
        setSurveys(data || []);
      } catch (e) {
        console.error("🔴 fnSources error:", e.code, e.message, e.details);
        setError("Failed to load data sources: " + e.message);
      } finally {
        setSourcesLoading(false);
      }
    })();
  }, []);

  const toggleSurvey = (id, checked) => {
    setSelectedSurveys(prev => {
      const next = new Set(prev);
      checked ? next.add(id) : next.delete(id);
      return next;
    });
  };

  // ── Submit ────────────────────────────────────────────────────────────────
  const handleSubmit = useCallback(async (override) => {
    const q = (override || question).trim();
    if (!q || selectedSurveys.size === 0) return;

    setError(""); setAnalyzing(true); setQuestion("");
    addLog(`Analyzing: "${q}"`);
    addLog(`Sources: ${selectedSurveys.size} survey(s)`);

    const prevHistory = history;
    setHistory(h => [...h, { role: "user", content: q }]);

    try {
      const { data } = await fnAnalyze({
        question: q,
        history: prevHistory,
        surveyIds: Array.from(selectedSurveys),
        canvasRequests: [],
        canvasDomain: "",
        includeCharts,
        forceRefresh,
      });

      const entry = {
        question: q,
        narrative: data.narrative,
        charts: data.charts || [],
        sourceSummary: data.sourceSummary || [],
      };

      setResults(prev => [...prev, entry]);
      setHistory(prev => [...prev, { role: "assistant", content: data.narrative }]);
      addLog("Done.", "success");
      setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: "smooth" }), 100);
    } catch (e) {
      console.error("🔴 Full error:", e);
      console.error("🔴 Code:", e.code);
      console.error("🔴 Details:", e.details);
      console.error("🔴 Message:", e.message);
      setError(`${e.code || ""}: ${e.message || "Analysis failed."}`);
      addLog(e.message, "error");
      setHistory(prevHistory);
    } finally {
      setAnalyzing(false);
    }
  }, [question, selectedSurveys, history]);

  // ── Download single result ────────────────────────────────────────────────
  const handleDownloadSingle = useCallback(async (idx) => {
    const entry = results[idx];
    const cardNode = cardRefs.current[idx];
    if (!entry) return;
    setPdfLoading(true);
    try {
      const JSPDF = await getJSPDF();
      const doc = new JSPDF({ orientation: "portrait", unit: "mm", format: "a4" });
      const W = doc.internal.pageSize.getWidth();
      const H = doc.internal.pageSize.getHeight();
      await buildResultPage(doc, W, H, entry, idx, results.length, cardNode);
      addFooters(doc, W, H);
      const safe = entry.question.replace(/\s+/g, "_").replace(/[^a-zA-Z0-9_]/g, "").slice(0, 40);
      doc.save(`KA_DataAnalyst_${safe}.pdf`);
    } catch (e) {
      setError("PDF failed: " + e.message);
    } finally {
      setPdfLoading(false);
    }
  }, [results]);

  // ── Download full session report ──────────────────────────────────────────
  const handleDownloadAll = useCallback(async () => {
    if (!results.length) return;
    setPdfLoading(true);
    try {
      const JSPDF = await getJSPDF();
      const doc = new JSPDF({ orientation: "portrait", unit: "mm", format: "a4" });
      const W = doc.internal.pageSize.getWidth();
      const H = doc.internal.pageSize.getHeight();
      const now = new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });

      // ── Cover page ──
      doc.setFillColor(...NAVY_P); doc.rect(0, 0, W, H, "F");
      doc.setFillColor(...GOLD_P); doc.rect(20, H / 2 - 2, W - 40, 1.5, "F");

      doc.setTextColor(212, 172, 106); doc.setFontSize(9); doc.setFont("helvetica", "bold");
      doc.text("KING'S ACADEMY  ·  INTERNAL REFERENCE", W / 2, H / 2 - 42, { align: "center" });

      doc.setTextColor(250, 247, 242); doc.setFontSize(22); doc.setFont("helvetica", "bold");
      doc.text("Data Analyst Report", W / 2, H / 2 - 26, { align: "center" });

      doc.setFontSize(13); doc.setFont("helvetica", "italic"); doc.setTextColor(200, 191, 176);
      doc.text("Survey Analysis · PII-Free", W / 2, H / 2 - 14, { align: "center" });

      // PII badge on cover
      doc.setFillColor(13, 45, 14);
      doc.roundedRect(W / 2 - 70, H / 2 + 4, 140, 13, 1, 1, "F");
      doc.setTextColor(63, 185, 80); doc.setFontSize(8); doc.setFont("helvetica", "bold");
      doc.text("PII-FREE — No student names or contact info in this report", W / 2, H / 2 + 12, { align: "center" });

      // Session stats
      doc.setFontSize(9); doc.setFont("helvetica", "normal"); doc.setTextColor(...MUTED_P);
      doc.text(`${results.length} result${results.length > 1 ? "s" : ""}  ·  ${now}`, W / 2, H / 2 + 26, { align: "center" });

      // Data sources on cover
      const allSources = [...new Set(results.flatMap(r => r.sourceSummary?.map(s => s.label) || []))];
      if (allSources.length > 0) {
        doc.setFontSize(8); doc.setTextColor(200, 191, 176);
        doc.text("Data sources:", W / 2, H / 2 + 36, { align: "center" });
        allSources.forEach((src, i) => {
          doc.setFontSize(7.5); doc.setTextColor(...GOLD_P);
          doc.text(`· ${src}`, W / 2, H / 2 + 43 + i * 6, { align: "center" });
        });
      }

      // ── Result pages ──
      for (let i = 0; i < results.length; i++) {
        doc.addPage();
        await buildResultPage(doc, W, H, results[i], i, results.length, cardRefs.current[i]);
      }

      // ── Conversation appendix ──
      if (history.length > 0) {
        doc.addPage();
        doc.setFillColor(...NAVY_P); doc.rect(0, 0, W, 24, "F");
        doc.setFillColor(...GOLD_P); doc.rect(0, 24, W, 0.8, "F");
        doc.setTextColor(250, 247, 242); doc.setFontSize(14); doc.setFont("helvetica", "bold");
        doc.text("Conversation Log", 14, 15);
        doc.setTextColor(212, 172, 106); doc.setFontSize(8);
        doc.text("All questions and AI responses from this session", W - 14, 15, { align: "right" });

        let y = 32;
        for (const msg of history) {
          const isUser = msg.role === "user";
          const cleanContent = msg.content
            .replace(/\*\*(.+?)\*\*/g, "$1")
            .replace(/\*(.+?)\*/g, "$1")
            .replace(/\[chart\]/g, "[chart shown in report]");
          const lines = doc.splitTextToSize(cleanContent, W - 36);
          const blockH = lines.length * 4.2 + 14;
          if (y + blockH > H - 16) { doc.addPage(); y = 20; }

          doc.setFillColor(isUser ? 232 : 244, 240, isUser ? 248 : 232);
          doc.rect(12, y, W - 24, blockH, "F");
          doc.setFillColor(...(isUser ? NAVY_P : GOLD_P));
          doc.rect(12, y, 2.5, blockH, "F");

          doc.setFontSize(7); doc.setFont("helvetica", "bold");
          doc.setTextColor(...(isUser ? NAVY_P : GOLD_P));
          doc.text(isUser ? "QUESTION" : "ANALYSIS", 18, y + 5.5);

          doc.setFontSize(8.5); doc.setFont("helvetica", "normal"); doc.setTextColor(...INK_P);
          doc.text(lines, 18, y + 11);
          y += blockH + 5;
        }
      }

      // ── Footers (skip cover) ──
      const pc = doc.internal.getNumberOfPages();
      for (let i = 2; i <= pc; i++) {
        doc.setPage(i);
        doc.setFillColor(244, 240, 232); doc.rect(0, H - 10, W, 10, "F");
        doc.setFontSize(7); doc.setFont("helvetica", "italic"); doc.setTextColor(...MUTED_P);
        doc.text("Internal use only · King's Academy Data Analyst · PII-Free Report", W / 2, H - 4, { align: "center" });
        doc.text(`${i - 1} / ${pc - 1}`, W - 14, H - 4);
      }

      doc.save(`KA_DataAnalystReport_${now.replace(/,?\s+/g, "_")}.pdf`);
    } catch (e) {
      setError("Report failed: " + e.message);
    } finally {
      setPdfLoading(false);
    }
  }, [results, history]);

  const hasResults     = results.length > 0;
  const canSubmit      = question.trim().length > 0 && selectedSurveys.size > 0 && !analyzing;
  const availableCount = surveys.filter(s => s.available).length;

  const SUGGESTIONS = [
    "Summarize overall sentiment across all questions",
    "Which questions had the most negative responses?",
    "Show a chart of responses to the top questions",
    "What are the main themes in open text responses?",
    "Compare response patterns across different surveys",
    "Which areas have the most room for improvement?",
  ];

  const S = {
    wrap:    { fontFamily: FONT_BODY, background: C.cream, minHeight: "100vh", color: C.ink },
    header:  { background: C.ink, padding: "2rem 2rem 1.6rem", borderBottom: `3px solid ${C.gold}` },
    inner:   { maxWidth: 1100, margin: "0 auto" },
    main:    { maxWidth: 1100, margin: "0 auto", padding: "2rem", display: "grid", gridTemplateColumns: "280px 1fr", gap: "1.5rem", alignItems: "start" },
    sidebar: { position: "sticky", top: "1rem" },
    card:    { background: "#fff", border: `1px solid ${C.border}`, borderRadius: 3, padding: "1.2rem", marginBottom: "1rem", boxShadow: "0 1px 6px rgba(0,0,0,0.05)" },
    label:   { display: "block", fontFamily: FONT_DATA, fontSize: "0.7rem", fontWeight: 600, letterSpacing: "0.1em", textTransform: "uppercase", color: C.muted, marginBottom: 6 },
    input:   { width: "100%", background: C.cream, border: `1px solid ${C.border}`, borderRadius: 2, padding: "8px 10px", color: C.ink, fontSize: "0.9rem", fontFamily: FONT_BODY, boxSizing: "border-box" },
    logBox:  { background: "#f8f6f2", border: `1px solid ${C.border}`, borderRadius: 2, padding: 10, maxHeight: 120, overflowY: "auto", fontSize: "0.7rem", fontFamily: FONT_DATA, lineHeight: 1.8 },
    pill:    { fontFamily: FONT_DATA, fontSize: 11, padding: "4px 11px", border: `1px solid ${C.border}`, borderRadius: 10, background: C.bg, color: C.muted, cursor: "pointer", whiteSpace: "nowrap" },
  };

  return (
    <div style={S.wrap}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,600;0,700;1,400&family=EB+Garamond:ital,wght@0,400;0,500;1,400&family=Rajdhani:wght@400;500;600&display=swap');
        * { box-sizing: border-box; }
        button { cursor: pointer; }
      `}</style>

      {/* ── Header ── */}
      <header style={S.header}>
        <div style={{ ...S.inner, display: "flex", justifyContent: "space-between", alignItems: "flex-end", flexWrap: "wrap", gap: 16 }}>
          <div>
            <div style={{ fontFamily: FONT_DATA, fontSize: 10, fontWeight: 600, letterSpacing: "0.25em", color: C.gold, textTransform: "uppercase", marginBottom: 8 }}>
              King's Academy · Data Analyst
            </div>
            <h1 style={{ fontFamily: FONT_DISPLAY, fontSize: "clamp(1.4rem,3vw,2rem)", fontWeight: 700, color: C.cream, margin: "0 0 6px", lineHeight: 1.15 }}>
              School <em style={{ color: C.gold }}>Intelligence</em>
            </h1>
            <p style={{ fontFamily: FONT_BODY, fontStyle: "italic", color: "rgba(250,247,242,0.5)", fontSize: 14, margin: 0 }}>
              Ask questions across SurveyMonkey, Canvas, and Veracross · PII-free analysis
            </p>
          </div>
          {hasResults && (
            <button
              onClick={handleDownloadAll}
              disabled={pdfLoading}
              style={{ fontFamily: FONT_DATA, fontSize: 12, fontWeight: 600, padding: "10px 20px", background: pdfLoading ? C.muted : C.gold, color: C.ink, border: "none", borderRadius: 2, opacity: pdfLoading ? 0.7 : 1, whiteSpace: "nowrap", flexShrink: 0 }}
            >
              {pdfLoading ? "Building…" : `↓ Full Report (${results.length} result${results.length > 1 ? "s" : ""})`}
            </button>
          )}
        </div>
      </header>

      <main style={S.main}>

        {/* ── Sidebar ── */}
        <div style={S.sidebar}>
          <div style={S.card}>
            <div style={{ fontFamily: FONT_DATA, fontSize: 9, fontWeight: 600, letterSpacing: "0.15em", textTransform: "uppercase", color: C.muted, marginBottom: 10 }}>
              Data Sources
            </div>

            {/* SurveyMonkey */}
            <div style={{ marginBottom: 12 }}>
              <div style={{ fontFamily: FONT_DATA, fontSize: 10, fontWeight: 600, letterSpacing: "0.1em", textTransform: "uppercase", color: C.navy, marginBottom: 6, display: "flex", justifyContent: "space-between" }}>
                <span>SurveyMonkey</span>
                {selectedSurveys.size > 0 && <span style={{ color: C.gold }}>{selectedSurveys.size} selected</span>}
              </div>

              {sourcesLoading ? (
                <div style={{ fontFamily: FONT_DATA, fontSize: 11, color: C.muted, padding: "8px 0" }}>Loading surveys…</div>
              ) : surveys.length === 0 ? (
                <div style={{ fontFamily: FONT_DATA, fontSize: 11, color: C.muted, padding: "8px 0" }}>
                  No surveys pulled yet. Use the SurveyMonkey tool first.
                </div>
              ) : (
                <div style={{ maxHeight: 320, overflowY: "auto" }}>
                  {surveys.map(s => (
                    <SurveyCheckbox key={s.id} survey={s} checked={selectedSurveys.has(s.id)} onChange={toggleSurvey} />
                  ))}
                </div>
              )}

              {availableCount > 0 && (
                <div style={{ display: "flex", gap: 6, marginTop: 8 }}>
                  <button onClick={() => setSelectedSurveys(new Set(surveys.filter(s => s.available).map(s => s.id)))} style={{ ...S.pill, fontSize: 10 }}>Select all</button>
                  <button onClick={() => setSelectedSurveys(new Set())} style={{ ...S.pill, fontSize: 10 }}>Clear</button>
                </div>
              )}
            </div>

            {/* Canvas placeholder */}
            <div style={{ borderTop: `1px solid ${C.border}`, paddingTop: 12 }}>
              <div style={{ fontFamily: FONT_DATA, fontSize: 10, fontWeight: 600, letterSpacing: "0.1em", textTransform: "uppercase", color: C.muted, marginBottom: 4 }}>
                Canvas <span style={{ fontWeight: 400, textTransform: "none", letterSpacing: 0 }}>(coming soon)</span>
              </div>
              <div style={{ fontFamily: FONT_DATA, fontSize: 10, color: C.muted }}>Available once Canvas API token is configured.</div>
            </div>

            {/* Veracross placeholder */}
            <div style={{ borderTop: `1px solid ${C.border}`, paddingTop: 12, marginTop: 12 }}>
              <div style={{ fontFamily: FONT_DATA, fontSize: 10, fontWeight: 600, letterSpacing: "0.1em", textTransform: "uppercase", color: C.muted, marginBottom: 4 }}>
                Veracross <span style={{ fontWeight: 400, textTransform: "none", letterSpacing: 0 }}>(coming soon)</span>
              </div>
              <div style={{ fontFamily: FONT_DATA, fontSize: 10, color: C.muted }}>SIS data joinable via sisId once connected.</div>
            </div>
          </div>

          {/* Activity log */}
          <div style={S.card}>
            <div style={{ fontFamily: FONT_DATA, fontSize: 9, fontWeight: 600, letterSpacing: "0.15em", textTransform: "uppercase", color: C.muted, marginBottom: 8 }}>Activity</div>
            <div style={S.logBox} ref={logRef}>
              {log.length === 0
                ? <span style={{ color: "#c8bfb0" }}>No activity yet.</span>
                : log.map((l, i) => (
                  <div key={i} style={{ color: l.type === "error" ? C.red : l.type === "success" ? C.green : C.muted }}>
                    <span style={{ color: "#c8bfb0" }}>{l.ts} </span>{l.msg}
                  </div>
                ))
              }
            </div>
          </div>
        </div>

        {/* ── Main content ── */}
        <div>
          {/* PII notice */}
          <div style={{ background: "#0d2d0e", border: "1px solid #238636", borderRadius: 3, padding: "8px 14px", marginBottom: 16, fontFamily: FONT_DATA, fontSize: "0.75rem", color: "#3fb950", display: "flex", gap: 8, alignItems: "center" }}>
            <span>🔒</span>
            <span>Server-side PII firewall active — names, emails, and contact info are stripped before any data reaches the AI.</span>
          </div>

          {error && (
            <div style={{ background: "#fff5f5", border: `1px solid ${C.red}`, borderRadius: 2, padding: "10px 14px", fontFamily: FONT_DATA, fontSize: 13, color: C.red, marginBottom: 16 }}>
              ⚠ {error}
            </div>
          )}

          {/* Result cards */}
          {results.map((entry, idx) => (
            <ResultCard
              key={idx}
              entry={entry}
              idx={idx}
              total={results.length}
              onDownload={handleDownloadSingle}
              pdfLoading={pdfLoading}
              cardRef={el => { cardRefs.current[idx] = el; }}
            />
          ))}

          {/* Loading */}
          {analyzing && (
            <div style={{ background: "#fff", border: `1px solid ${C.border}`, borderRadius: 3, padding: 32, textAlign: "center", marginBottom: 20 }}>
              <div style={{ fontFamily: FONT_DISPLAY, fontSize: "1.05rem", color: C.muted, fontStyle: "italic", marginBottom: 6 }}>Analyzing…</div>
              <div style={{ fontFamily: FONT_DATA, fontSize: 11, color: "#c8bfb0" }}>Loading data · applying PII firewall · generating insights</div>
            </div>
          )}

          {/* ── Query box ── */}
          <div style={{ background: "#fff", border: `1px solid ${C.border}`, borderRadius: 3, overflow: "hidden", boxShadow: "0 1px 8px rgba(0,0,0,0.05)" }}>
            <div style={{ background: C.bg, borderBottom: `1px solid ${C.border}`, padding: "12px 20px", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
                <div style={{ fontFamily: FONT_DATA, fontSize: 10, fontWeight: 600, letterSpacing: "0.18em", textTransform: "uppercase", color: C.muted }}>
                {hasResults ? "Ask a follow-up or new question" : "Ask anything about your data"}
              </div>
              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                {/* Chart toggle */}
                <button
                  onClick={() => setIncludeCharts(v => !v)}
                  title={includeCharts ? "Charts enabled — click to disable" : "Charts disabled — click to enable"}
                  style={{ fontFamily: FONT_DATA, fontSize: 11, fontWeight: 600, padding: "5px 12px", border: `1px solid ${includeCharts ? C.gold : C.border}`, borderRadius: 2, background: includeCharts ? "#fff8ed" : C.cream, color: includeCharts ? C.gold : C.muted, display: "flex", alignItems: "center", gap: 5 }}
                >
                  <span style={{ fontSize: 13 }}>📊</span>
                  {includeCharts ? "Charts On" : "Charts Off"}
                </button>
                {/* Force refresh toggle */}
                <button
                  onClick={() => setForceRefresh(v => !v)}
                  title={forceRefresh ? "Force refresh on — will skip cache and fetch fresh answer" : "Cache enabled — click to force a fresh answer"}
                  style={{ fontFamily: FONT_DATA, fontSize: 11, fontWeight: 600, padding: "5px 12px", border: `1px solid ${forceRefresh ? C.red : C.border}`, borderRadius: 2, background: forceRefresh ? "#fff5f5" : C.cream, color: forceRefresh ? C.red : C.muted, display: "flex", alignItems: "center", gap: 5 }}
                >
                  <span style={{ fontSize: 13 }}>🔄</span>
                  {forceRefresh ? "Fresh" : "Cached"}
                </button>
                {hasResults && (
                  <button
                    onClick={() => setShowThread(s => !s)}
                    style={{ fontFamily: FONT_DATA, fontSize: 11, fontWeight: 600, padding: "5px 14px", border: `1px solid ${showThread ? C.navy : C.border}`, borderRadius: 2, background: showThread ? "#e8f0f8" : C.cream, color: showThread ? C.navy : C.muted }}
                  >
                    {showThread ? "▲ Hide Thread" : "▼ View Thread"}
                  </button>
                )}
              </div>
            </div>

            {/* Conversation thread */}
            {showThread && history.length > 0 && (
              <div style={{ borderBottom: `1px solid ${C.border}`, maxHeight: 260, overflowY: "auto", padding: "14px 20px", background: "#fdfcf8", display: "flex", flexDirection: "column", gap: 10 }}>
                {history.map((msg, i) => (
                  <div key={i} style={{ display: "flex", gap: 10 }}>
                    <div style={{ flexShrink: 0, width: 26, height: 26, borderRadius: "50%", background: msg.role === "user" ? C.navy : C.gold, display: "flex", alignItems: "center", justifyContent: "center", fontFamily: FONT_DATA, fontSize: 9, fontWeight: 700, color: "#fff" }}>
                      {msg.role === "user" ? "Q" : "AI"}
                    </div>
                    <div style={{ fontFamily: FONT_BODY, fontSize: 13, color: C.ink, lineHeight: 1.6, background: C.bg, border: `1px solid ${C.border}`, borderRadius: 2, padding: "8px 12px", flex: 1 }}>
                      {msg.content.slice(0, 400)}{msg.content.length > 400 ? "…" : ""}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Input */}
            <div style={{ padding: "16px 20px" }}>
              {selectedSurveys.size === 0 && (
                <div style={{ fontFamily: FONT_DATA, fontSize: 11, color: C.muted, marginBottom: 10, padding: "8px 12px", background: C.bg, borderRadius: 2, border: `1px solid ${C.border}` }}>
                  ← Select at least one data source from the sidebar to begin
                </div>
              )}
              <textarea
                value={question}
                onChange={e => setQuestion(e.target.value)}
                onKeyDown={e => { if (e.key === "Enter" && (e.metaKey || e.ctrlCmd)) handleSubmit(); }}
                placeholder={selectedSurveys.size === 0 ? "Select a data source first…" : hasResults ? "Refine, dig deeper, or ask something new… (⌘+Enter to send)" : "e.g. Summarize overall sentiment and show a chart (⌘+Enter to send)"}
                rows={3}
                disabled={selectedSurveys.size === 0}
                style={{ ...S.input, marginBottom: 12, resize: "vertical", lineHeight: 1.55 }}
              />

              <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 12 }}>
                {SUGGESTIONS.slice(0, 4).map(s => (
                  <button key={s} style={{ ...S.pill, opacity: selectedSurveys.size === 0 ? 0.4 : 1 }} onClick={() => handleSubmit(s)} disabled={!canSubmit}>
                    {s}
                  </button>
                ))}
              </div>

              <div style={{ display: "flex", justifyContent: "flex-end" }}>
                <button
                  onClick={() => handleSubmit()}
                  disabled={!canSubmit}
                  style={{ padding: "9px 24px", borderRadius: 2, border: "none", fontFamily: FONT_DATA, fontSize: "0.9rem", fontWeight: 600, background: canSubmit ? C.navy : C.muted, color: C.cream }}
                >
                  {analyzing ? "Analyzing…" : hasResults ? "Send →" : "Analyze →"}
                </button>
              </div>
            </div>
          </div>

          {/* Empty state */}
          {!hasResults && !analyzing && (
            <div style={{ marginTop: 16, padding: "16px 20px", background: C.bg, border: `1px solid ${C.border}`, borderRadius: 2, fontFamily: FONT_BODY, fontSize: 13, color: C.muted, lineHeight: 1.75 }}>
              <strong style={{ fontFamily: FONT_DATA, fontSize: 9, letterSpacing: "0.15em", textTransform: "uppercase", display: "block", marginBottom: 6 }}>How it works</strong>
              Select one or more surveys from the sidebar, then ask anything in plain English.
              The AI loads aggregated response data from Firestore, applies a PII firewall server-side,
              and returns narrative analysis with charts where useful. Export any single result or the
              full session as a formatted PDF — including a cover page, per-result analysis, charts,
              and a full conversation log.
            </div>
          )}

          <div ref={bottomRef} />
        </div>
      </main>
    </div>
  );
}